import type { NotificationType } from '@crew/shared';
import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdmin } from '@/auth/AuthProvider';
import { ArchiveIcon, BellIcon, ChatIcon, ContractorsIcon, EditIcon, LockIcon, RequestsIcon } from '@/components/icons';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { Notice } from '@/components/ui/Notice';
import { PageHeader } from '@/components/ui/PageHeader';
import { Spinner } from '@/components/ui/Spinner';
import { useToast } from '@/components/ui/Toast';
import { useNotifications, type NotificationView } from '@/hooks/useAdminData';
import { formatWhen } from '@/lib/activity';
import { tsToDate } from '@/lib/ts';
import { adminApi, readerUid } from '@/services/adminApi';
import { errorMessage } from '@/services/apiError';

const ICONS: Record<NotificationType, ReactNode> = {
  registration: <ContractorsIcon size={19} />,
  change_request: <RequestsIcon size={19} />,
  registration_closed: <LockIcon size={19} />,
  admin_change: <EditIcon size={19} />,
  message: <ChatIcon size={19} />,
  team_message: <ChatIcon size={19} />,
};

const RESOLUTION_TEXT = { approved: 'אושרה', rejected: 'נדחתה', cancelled: 'בוטלה' } as const;

/** מרכז ההתראות. "נקרא" נשמר לכל מנהל בנפרד; בקשה שטופלה מראה מי טיפל בה. */
export function NotificationsPage() {
  const { user } = useAdmin();
  const uid = readerUid(user);
  const list = useNotifications();
  const navigate = useNavigate();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const unread = list.state === 'ready' ? list.data.filter((n) => !n.readBy?.[uid]).length : 0;

  async function open(n: NotificationView) {
    if (!n.readBy?.[uid]) void adminApi(user).markRead([n.id]).catch(() => undefined);
    navigate(n.link || '/');
  }

  return (
    <>
      <PageHeader
        title="התראות"
        subtitle={unread ? `${unread} שלא נקראו` : 'הכל נקרא'}
        actions={
          unread > 0 && (
            <Button
              variant="secondary"
              className="min-h-10 px-3 text-[14px]"
              loading={busy}
              onClick={async () => {
                setBusy(true);
                try {
                  await adminApi(user).markRead('all');
                } catch (err) {
                  toast(errorMessage(err), 'error');
                } finally {
                  setBusy(false);
                }
              }}
            >
              סימון הכל כנקרא
            </Button>
          )
        }
      />

      {list.state === 'loading' && (
        <div className="flex justify-center py-16">
          <Spinner />
        </div>
      )}
      {list.state === 'error' && <Notice tone="error">{list.message}</Notice>}
      {list.state === 'ready' && list.data.length === 0 && (
        <EmptyState icon={<BellIcon size={30} />} title="אין התראות" body="כאן יופיעו הרשמות חדשות, בקשות שינוי, סגירת הרשמה ושינויים של מנהלים אחרים." />
      )}
      {list.state === 'ready' && list.data.length > 0 && (
        <ul className="divide-y divide-line overflow-hidden rounded-card border border-line bg-surface">
          {list.data.map((n) => {
            const isUnread = !n.readBy?.[uid];
            const when = tsToDate(n.createdAt);
            return (
              <li key={n.id}>
                <button type="button" onClick={() => open(n)} className={`flex w-full gap-3 px-4 py-3.5 text-start hover:bg-sunken/60 ${isUnread ? 'bg-brand-soft/40' : ''}`}>
                  <span className={`mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-full ${isUnread ? 'bg-brand text-white' : 'bg-sunken text-ink-2'}`}>
                    {ICONS[n.type] ?? <ArchiveIcon size={19} />}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-baseline justify-between gap-3">
                      <span className={`text-[15px] ${isUnread ? 'font-bold' : 'font-semibold text-ink-2'}`}>{n.title}</span>
                      {when && <time className="tabular shrink-0 text-[12px] text-ink-3">{formatWhen(when)}</time>}
                    </span>
                    <span className="mt-0.5 block text-[14px] text-ink-2">{n.body}</span>
                    {n.type === 'change_request' && (
                      <span className={`mt-1.5 inline-block rounded-full px-2 py-0.5 text-[12px] font-semibold ${n.resolution ? 'bg-sunken text-ink-2' : 'bg-warn-soft text-warn'}`}>
                        {n.resolution ? `${RESOLUTION_TEXT[n.resolution]}${n.resolvedByName ? ` · ${n.resolvedByName}` : ''}` : 'ממתינה לטיפול'}
                      </span>
                    )}
                  </span>
                  {isUnread && <span className="mt-2 size-2.5 shrink-0 rounded-full bg-accent" aria-label="לא נקרא" />}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </>
  );
}
